import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';

interface HeadlineDisplayProps {
  headline: HeadlineOverlay;
  currentTime: number;
  forceShow?: boolean;
}

export const HeadlineDisplay: React.FC<HeadlineDisplayProps> = ({ 
  headline, 
  currentTime,
  forceShow = false
}) => {
  useLanguage();
  if (!headline?.enabled) return null;
  
  // Check timing constraints (skip in forceShow mode for preview)
  const parseTime = (t: string | number | undefined): number => {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    const [m, s] = t.split(':');
    return (parseInt(m) || 0) * 60 + (parseInt(s) || 0);
  };

  const showThroughout = headline.timing?.showThroughout ?? (!headline.timing?.start && !headline.timing?.end);
  const startTime = parseTime(headline.timing?.start);
  const endTime = parseTime(headline.timing?.end);

  if (!forceShow && !showThroughout) {
    if (currentTime < startTime) return null;
    if (endTime > 0 && currentTime > endTime) return null;
  }

  // Get position classes
  const getPositionClasses = () => {
    switch (headline.position) {
      case 'top':
        return 'top-[10%]';
      case 'center':
        return 'top-1/2 -translate-y-1/2';
      case 'bottom':
      default:
        return 'bottom-4';
    }
  };

  // Get font size in pixels
  const getFontSize = (size?: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small': return '16px';
      case 'medium': return '20px';
      case 'large': return '28px';
      default: return '20px';
    }
  };

  // Get font weight
  const getFontWeight = (weight?: string) => {
    if (weight === 'bold') return '700';
    return '400'; // normal
  };

  // Image mode - SEM fallback (desktop e mobile independentes)
  if (headline.imageMode?.enabled) {
    const isMobile = window.innerWidth < 768;
    const imageUrl = isMobile 
      ? headline.imageMode.mobileImage
      : headline.imageMode.desktopImage;

    // Se não tem imagem para o dispositivo atual, não renderiza nada
    if (!imageUrl) return null;

    return (
      <div 
        className={`absolute ${getPositionClasses()} left-1/2 transform -translate-x-1/2 text-center pointer-events-none animate-fade-in`} 
        style={{ zIndex: 10, width: '100%' }}
      >
        <img 
          src={imageUrl} 
          alt={translateInterfaceText("Headline")}
          className="max-w-[90%] mx-auto"
          style={{ maxHeight: '120px' }}
        />
      </div>
    );
  }

  // HTML mode
  if (headline.htmlMode?.enabled && headline.htmlMode.code) {
    return (
      <div 
        className={`absolute ${getPositionClasses()} left-1/2 transform -translate-x-1/2 text-center pointer-events-none animate-fade-in`}
        style={{ zIndex: 10, maxWidth: '90%' }}
        dangerouslySetInnerHTML={{ __html: headline.htmlMode.code }}
      />
    );
  }

  // Text mode (only render if explicitly enabled or by default)
  const isTextMode = headline.textMode?.enabled ?? (!headline.htmlMode.enabled && !headline.imageMode.enabled);
  if (!isTextMode) return null;
  const hasBackground = headline.background?.enabled;
  const backgroundColor = hasBackground ? 
    headline.background?.color || 'rgba(0, 0, 0, 0.7)' : 
    'transparent';

  return (
    <div 
      className={`absolute ${getPositionClasses()} left-1/2 transform -translate-x-1/2 text-center pointer-events-none animate-fade-in`} 
      style={{ 
        zIndex: 10, 
        maxWidth: '90%',
        backgroundColor,
        padding: hasBackground ? '8px 12px' : '0',
        borderRadius: hasBackground ? '4px' : '0',
      }}
    >
      {headline.headline?.text && (
        <p
          ref={(el) => {
            if (el) {
              el.style.setProperty('font-size', getFontSize(headline.headline.size), 'important');
            }
          }}
          style={{
            color: headline.headline.color,
            fontFamily: headline.headline.fontFamily,
            fontWeight: getFontWeight(headline.headline.fontWeight),
            margin: 0,
          }}
        >
          {headline.headline.text}
        </p>
      )}

      {headline.subheadline?.text && (
        <p
          ref={(el) => {
            if (el) {
              el.style.setProperty('font-size', getFontSize(headline.subheadline.size), 'important');
            }
          }}
          style={{
            color: headline.subheadline.color,
            fontFamily: headline.subheadline.fontFamily,
            fontWeight: getFontWeight(headline.subheadline.fontWeight),
            margin: '4px 0 0 0',
          }}
        >
          {headline.subheadline.text}
        </p>
      )}
    </div>
  );
};